import { useState, useEffect } from "react";
import api from "../../api/client";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import CopyResumeLink from "../../components/CopyResumeLink";
import { ENTRY_TYPE_DOT_CLASS, VALIDATION_BADGES } from "../../constants/resumeColors";

const ENTRY_TYPES = [
  { value: "experience", label: "Work experience" },
  { value: "education", label: "Education" },
  { value: "certification", label: "Certification" },
  { value: "project", label: "Project" },
  { value: "volunteer", label: "Volunteer work" },
  { value: "achievement", label: "Achievement" },
  { value: "publication", label: "Publication" },
];

const ENTRY_TYPE_ICONS = {
  experience: "💼",
  education: "🎓",
  certification: "📜",
  project: "🚀",
  volunteer: "🤝",
  achievement: "🏆",
  publication: "📄",
};

function ValidationBadge({ status }) {
  const badge = VALIDATION_BADGES[status] ?? VALIDATION_BADGES.self_reported;
  return (
    <span
      className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.color}`}
    >
      {badge.label}
    </span>
  );
}

function formatDateRange(entry) {
  if (!entry.started_at) return null;
  const start = new Date(entry.started_at).getFullYear();
  if (entry.is_current) return `${start} – Present`;
  if (entry.ended_at)
    return `${start} – ${new Date(entry.ended_at).getFullYear()}`;
  return `${start}`;
}

function sortTimeline(entries) {
  return [...entries].sort((a, b) => {
    if (a.is_current && !b.is_current) return -1;
    if (!a.is_current && b.is_current) return 1;
    if (!a.started_at && !b.started_at) return 0;
    if (!a.started_at) return 1;
    if (!b.started_at) return 1;
    return new Date(b.started_at) - new Date(a.started_at);
  });
}

// ── Confirm Modal ─────────────────────────────────────────

function ConfirmModal({
  title,
  message,
  confirmLabel = "Delete",
  onConfirm,
  onCancel,
}) {
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onCancel} />
      <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
        <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 shadow-2xl w-full max-w-sm p-6">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
              <svg
                className="w-5 h-5 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-white mb-1">
                {title}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                {message}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2.5 rounded-lg text-sm font-medium transition"
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Entry Panel ───────────────────────────────────────────

function EntryPanel({ entry, onClose, onSave }) {
  const isNew = !entry?.id;
  const [form, setForm] = useState({
    entry_type: entry?.entry_type ?? "experience",
    title: entry?.title ?? "",
    organisation: entry?.organisation ?? "",
    location: entry?.location ?? "",
    started_at: entry?.started_at ? entry.started_at.slice(0, 10) : "",
    ended_at: entry?.ended_at ? entry.ended_at.slice(0, 10) : "",
    is_current: entry?.is_current ?? false,
    description: entry?.description ?? "",
    url: entry?.url ?? "",
    credential_id: entry?.credential_id ?? "",
    issuer: entry?.issuer ?? "",
    display_order: entry?.display_order ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        started_at: form.started_at
          ? new Date(form.started_at).toISOString()
          : null,
        ended_at: form.is_current
          ? null
          : form.ended_at
            ? new Date(form.ended_at).toISOString()
            : null,
      };
      if (isNew) {
        await api.post("/resume/timeline", payload);
      } else {
        await api.patch(`/resume/timeline/${entry.id}`, payload);
      }
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail ?? err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark w-full";
  const labelClass =
    "block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5";

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-brand-dark-surface shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/10 flex-shrink-0">
          <h2 className="font-semibold text-gray-900 dark:text-white">
            {isNew ? "Add timeline entry" : "Edit entry"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          id="entry-form"
          className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4"
        >
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}
          <div>
            <label className={labelClass}>Type *</label>
            <select
              value={form.entry_type}
              onChange={(e) => setForm({ ...form, entry_type: e.target.value })}
              className={inputClass}
            >
              {ENTRY_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Title *</label>
            <input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={
                form.entry_type === "experience"
                  ? "e.g. Senior Product Manager"
                  : "e.g. BSc Computer Science"
              }
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>
              {form.entry_type === "certification"
                ? "Issuing organisation"
                : "Organisation / Company"}
            </label>
            <input
              value={form.organisation}
              onChange={(e) =>
                setForm({ ...form, organisation: e.target.value })
              }
              placeholder="e.g. Spotify"
              className={inputClass}
            />
          </div>
          {form.entry_type === "certification" && (
            <>
              <div>
                <label className={labelClass}>Credential ID</label>
                <input
                  value={form.credential_id}
                  onChange={(e) =>
                    setForm({ ...form, credential_id: e.target.value })
                  }
                  placeholder="e.g. ABC-123456"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Credential URL</label>
                <input
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://..."
                  className={inputClass}
                />
              </div>
            </>
          )}
          <div>
            <label className={labelClass}>Location</label>
            <input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="e.g. Stockholm, Sweden"
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Start date</label>
              <input
                type="date"
                value={form.started_at}
                onChange={(e) =>
                  setForm({ ...form, started_at: e.target.value })
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>End date</label>
              <input
                type="date"
                value={form.ended_at}
                disabled={form.is_current}
                onChange={(e) => setForm({ ...form, ended_at: e.target.value })}
                className={`${inputClass} ${form.is_current ? "opacity-40" : ""}`}
              />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={form.is_current}
              onChange={(e) =>
                setForm({ ...form, is_current: e.target.checked, ended_at: "" })
              }
              className="rounded border-gray-300 text-brand-violet dark:text-brand-violet-on-dark focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
            />
            Currently{" "}
            {form.entry_type === "experience" ? "working here" : "ongoing"}
          </label>
          <div>
            <label className={labelClass}>Description</label>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              placeholder="Describe your role, achievements, responsibilities..."
              rows={4}
              className={`${inputClass} resize-none`}
            />
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-100 dark:border-white/10 flex gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="entry-form"
            disabled={saving}
            className="flex-1 bg-brand-violet text-white py-2.5 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition"
          >
            {saving ? "Saving..." : isNew ? "Add entry" : "Save changes"}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Resume Panel ──────────────────────────────────────────

function ResumePanel({ resume, onClose, onSave, timeline }) {
  const isNew = !resume?.id;
  const [form, setForm] = useState({
    name: resume?.name ?? "",
    summary: resume?.summary ?? "",
    is_primary: resume?.is_primary ?? false,
    is_public: resume?.is_public ?? false,
  });
  const [selectedEntries, setSelectedEntries] = useState(new Set());
  const [loadingSections, setLoadingSections] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isNew) return;
    api
      .get(`/resume/resumes/${resume.id}/sections`)
      .then(({ data }) =>
        setSelectedEntries(new Set(data.map((s) => s.timeline_entry_id))),
      )
      .catch(() => {})
      .finally(() => setLoadingSections(false));
  }, [resume?.id]);

  const toggleEntry = (id) => {
    setSelectedEntries((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      let resumeId;
      if (isNew) {
        const { data } = await api.post("/resume/resumes", form);
        resumeId = data.id;
        await Promise.all(
          [...selectedEntries].map((entryId) =>
            api.post(`/resume/resumes/${resumeId}/sections/${entryId}`),
          ),
        );
      } else {
        await api.patch(`/resume/resumes/${resume.id}`, form);
        resumeId = resume.id;
        const { data: currentSections } = await api.get(
          `/resume/resumes/${resumeId}/sections`,
        );
        const currentIds = new Set(
          currentSections.map((s) => s.timeline_entry_id),
        );
        const toAdd = [...selectedEntries].filter((id) => !currentIds.has(id));
        const toRemove = [...currentIds].filter(
          (id) => !selectedEntries.has(id),
        );
        await Promise.all([
          ...toAdd.map((id) =>
            api.post(`/resume/resumes/${resumeId}/sections/${id}`),
          ),
          ...toRemove.map((id) =>
            api.delete(`/resume/resumes/${resumeId}/sections/${id}`),
          ),
        ]);
      }
      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail ?? err.message);
    } finally {
      setSaving(false);
    }
  };

  const sortedTimeline = sortTimeline(timeline);
  const inputClass =
    "border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark w-full";
  const labelClass =
    "block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5";

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-brand-dark-surface shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/10 flex-shrink-0">
          <h2 className="font-semibold text-gray-900 dark:text-white">
            {isNew ? "Create resume" : "Edit resume"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          id="resume-form"
          className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4"
        >
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}
          <div>
            <label className={labelClass}>Resume name *</label>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Product Management, Technical Lead, General"
              className={inputClass}
            />
            <p className="text-xs text-gray-400 mt-1">
              Describe the type of role this resume is tailored for
            </p>
          </div>
          <div>
            <label className={labelClass}>Summary / headline</label>
            <textarea
              value={form.summary}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
              placeholder="A brief professional summary shown at the top of this resume..."
              rows={3}
              className={`${inputClass} resize-none`}
            />
          </div>
          <div className="flex flex-col gap-2 pt-2 border-t border-gray-100 dark:border-white/10">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 dark:text-gray-300">
              <input
                type="checkbox"
                checked={form.is_primary}
                onChange={(e) =>
                  setForm({
                    ...form,
                    is_primary: e.target.checked,
                    // The account link points at the primary resume, so a
                    // private primary would leave that link resolving to
                    // something else. Ticking primary implies shareable.
                    is_public: e.target.checked ? true : form.is_public,
                  })
                }
                className="rounded border-gray-300 text-brand-violet dark:text-brand-violet-on-dark focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
              />
              Set as primary resume
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 dark:text-gray-300">
              <input
                type="checkbox"
                checked={form.is_public}
                onChange={(e) =>
                  setForm({ ...form, is_public: e.target.checked })
                }
                className="rounded border-gray-300 text-brand-violet dark:text-brand-violet-on-dark focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
              />
              Make publicly shareable (enables link + QR code)
            </label>
          </div>
          <div className="pt-2 border-t border-gray-100 dark:border-white/10">
            <div className="flex items-center justify-between mb-2">
              <label className={`${labelClass} mb-0`}>
                {isNew ? "Attach timeline entries" : "Manage timeline entries"}
              </label>
              {timeline.length > 0 && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedEntries(new Set(timeline.map((e) => e.id)))
                    }
                    className="text-xs text-brand-violet dark:text-brand-violet-on-dark hover:underline"
                  >
                    Select all
                  </button>
                  <span className="text-gray-300 dark:text-gray-600 text-xs">
                    ·
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedEntries(new Set())}
                    className="text-xs text-gray-400 hover:underline"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
            {timeline.length === 0 ? (
              <p className="text-xs text-gray-400 py-2">
                Add entries to your timeline first — then attach them to
                resumes.
              </p>
            ) : loadingSections ? (
              <div className="text-xs text-gray-400 py-4 text-center">
                Loading attached entries...
              </div>
            ) : (
              <>
                <p className="text-xs text-gray-400 mb-3">
                  {isNew
                    ? "Choose which entries to include. You can always manage them later."
                    : "Check entries to include, uncheck to remove from this resume."}
                </p>
                <div className="flex flex-col gap-2 max-h-72 overflow-y-auto pr-1">
                  {sortedTimeline.map((entry) => {
                    const selected = selectedEntries.has(entry.id);
                    const dateRange = formatDateRange(entry);
                    return (
                      <div
                        key={entry.id}
                        onClick={() => toggleEntry(entry.id)}
                        className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                          selected
                            ? "border-brand-violet dark:border-brand-violet-on-dark bg-brand-violet/5 dark:bg-brand-violet/10"
                            : "border-gray-100 dark:border-white/10 hover:border-gray-200 dark:hover:border-white/20 bg-gray-50 dark:bg-brand-dark-bg"
                        }`}
                      >
                        <div
                          className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm flex-shrink-0 transition ${
                            selected
                              ? "bg-brand-violet text-white"
                              : "bg-white dark:bg-brand-dark-surface"
                          }`}
                        >
                          {selected
                            ? "✓"
                            : (ENTRY_TYPE_ICONS[entry.entry_type] ?? "📋")}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {entry.title}
                          </p>
                          <p className="text-xs text-gray-400 truncate">
                            {entry.organisation && entry.organisation}
                            {entry.organisation && dateRange && " · "}
                            {dateRange && dateRange}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {selectedEntries.size > 0 && (
                  <p className="text-xs text-brand-violet dark:text-brand-violet-on-dark mt-2">
                    {selectedEntries.size} entr
                    {selectedEntries.size === 1 ? "y" : "ies"} selected
                  </p>
                )}
              </>
            )}
          </div>
        </form>

        <div className="px-6 py-4 border-t border-gray-100 dark:border-white/10 flex gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="resume-form"
            disabled={saving}
            className="flex-1 bg-brand-violet text-white py-2.5 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition"
          >
            {saving ? "Saving..." : isNew ? "Create resume" : "Save changes"}
          </button>
        </div>
      </div>
    </>
  );
}

// ── QR Modal ──────────────────────────────────────────────

function QRModal({ resume, onClose }) {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requiresPro, setRequiresPro] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api
      .get(`/resume/resumes/${resume.id}/qr`)
      .then(({ data }) => setQrData(data))
      .catch((err) => {
        if (err.response?.status === 403) {
          setRequiresPro(true);
        }
      })
      .finally(() => setLoading(false));
  }, [resume.id]);

  const handleCopy = () => {
    navigator.clipboard.writeText(qrData.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
        <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 shadow-2xl w-full max-w-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-semibold text-gray-900 dark:text-white">
              Share resume
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          {loading ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              Generating QR code...
            </div>
          ) : qrData ? (
            <>
              <div className="flex justify-center mb-5">
                <div className="p-3 bg-white rounded-xl border border-gray-100 shadow-sm">
                  <img
                    src={`data:image/png;base64,${qrData.qr_base64}`}
                    alt="QR code"
                    className="w-40 h-40"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-400 mb-2 text-center">
                Shareable link
              </p>
              <div className="flex gap-2 mb-4">
                <input
                  readOnly
                  value={qrData.url}
                  className="flex-1 border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-brand-dark-bg rounded-lg px-3 py-2 text-xs text-gray-600 dark:text-gray-300 font-mono"
                />
                <button
                  onClick={handleCopy}
                  className="flex-shrink-0 border border-brand-violet dark:border-brand-violet-on-dark text-brand-violet dark:text-brand-violet-on-dark px-3 py-2 rounded-lg text-xs hover:bg-brand-violet-tint dark:hover:bg-brand-violet/10 transition"
                >
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
              <p className="text-xs text-gray-400 text-center">
                Scan QR or share link to show this resume publicly. Disable
                sharing anytime by making the resume private.
              </p>
            </>
          ) : requiresPro ? (
            <p className="text-center text-gray-400 text-sm py-4">
              QR code sharing is a Joborigo Pro feature.
            </p>
          ) : (
            <p className="text-center text-gray-400 text-sm py-4">
              Could not generate QR code.
            </p>
          )}
        </div>
      </div>
    </>
  );
}

// ── Main component ────────────────────────────────────────

export default function ResumeBuilder() {
  const [activeTab, setActiveTab] = useState("timeline");
  const [timeline, setTimeline] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(true);
  const [loadingResumes, setLoadingResumes] = useState(true);
  const [copiedSlug, setCopiedSlug] = useState(null);
  const [filterType, setFilterType] = useState("All");
  const [editingEntry, setEditingEntry] = useState(null);
  const [showEntryPanel, setShowEntryPanel] = useState(false);
  const [editingResume, setEditingResume] = useState(null);
  const [showResumePanel, setShowResumePanel] = useState(false);
  const [qrResume, setQrResume] = useState(null);

  // Confirm modal state
  const [deleteEntryTarget, setDeleteEntryTarget] = useState(null);
  const [deleteResumeTarget, setDeleteResumeTarget] = useState(null);

  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleImportCV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/resume/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      fetchTimeline();
      fetchResumes();
    } catch (err) {
      alert(err.response?.data?.detail ?? "Import failed. Please try again.");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const fetchTimeline = async () => {
    try {
      const { data } = await api.get("/resume/timeline");
      setTimeline(data);
    } catch {
    } finally {
      setLoadingTimeline(false);
    }
  };

  const fetchResumes = async () => {
    try {
      const { data } = await api.get("/resume/resumes");
      setResumes(data);
    } catch {
    } finally {
      setLoadingResumes(false);
    }
  };

  useEffect(() => {
    document.title = "Joborigo — Resume Builder";
  }, []);
  useEffect(() => {
    fetchTimeline();
    fetchResumes();
  }, []);

  const handleDeleteEntryConfirmed = async () => {
    try {
      await api.delete(`/resume/timeline/${deleteEntryTarget.id}`);
      setDeleteEntryTarget(null);
      fetchTimeline();
    } catch (err) {
      alert(err.response?.data?.detail ?? err.message);
    }
  };

  const handleDeleteResumeConfirmed = async () => {
    try {
      await api.delete(`/resume/resumes/${deleteResumeTarget.id}`);
      setDeleteResumeTarget(null);
      fetchResumes();
    } catch (err) {
      alert(err.response?.data?.detail ?? err.message);
    }
  };

  const filteredTimeline =
    filterType === "All"
      ? timeline
      : timeline.filter(
          (e) =>
            e.entry_type ===
            ENTRY_TYPES.find((t) => t.label === filterType)?.value,
        );

  const sortedTimeline = sortTimeline(filteredTimeline);

  const tabs = [
    { value: "timeline", label: "My Timeline" },
    { value: "resumes", label: "My Resumes" },
    { value: "arena", label: "Arena" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <NavBar currentPage="resume" />

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10 w-full">
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-1">Resume Builder</h1>
          <p className="text-gray-400 text-sm">
            One timeline. Multiple tailored resumes. Your story, your way.
          </p>
        </div>

        <div className="flex gap-2 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                activeTab === tab.value
                  ? "bg-brand-violet text-white"
                  : "bg-white dark:bg-brand-dark-surface text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-white/10 hover:border-brand-violet dark:hover:border-brand-violet-on-dark hover:text-brand-violet dark:hover:text-brand-violet-on-dark"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {importResult && (
          <div className="mb-6 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-5 py-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-green-800 dark:text-green-300 mb-1">
                  CV imported successfully
                </p>
                <p className="text-sm text-green-700 dark:text-green-400">
                  {importResult.parse_notes}
                </p>
                <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                  {importResult.entries_created} entries added as draft. Review
                  and refine before publishing.
                </p>
              </div>
              <button
                onClick={() => setImportResult(null)}
                className="text-green-600 dark:text-green-400 hover:text-green-800 flex-shrink-0 text-lg leading-none"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* ── Timeline tab ── */}
        {activeTab === "timeline" && (
          <div>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  Professional Timeline
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  The single source of truth for all your resumes · Newest first
                </p>
              </div>
              <div className="flex gap-2">
                <label
                  className="cursor-pointer border border-brand-violet dark:border-brand-violet-on-dark text-brand-violet dark:text-brand-violet-on-dark px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-violet-tint dark:hover:bg-brand-violet/10 transition flex items-center gap-2"
                  title="Upload Positions.csv from your LinkedIn data export"
                >
                  <input
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={handleImportCV}
                  />
                  {importing ? "Importing..." : "Import from LinkedIn"}
                </label>
                <button
                  onClick={() => {
                    setEditingEntry(null);
                    setShowEntryPanel(true);
                  }}
                  className="bg-brand-violet text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition flex items-center gap-2"
                >
                  <span className="text-lg leading-none">+</span> Add entry
                </button>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap mb-6">
              {["All", ...ENTRY_TYPES.map((t) => t.label)].map((label) => (
                <button
                  key={label}
                  onClick={() => setFilterType(label)}
                  className={`text-xs px-3 py-1 rounded-full border transition ${
                    filterType === label
                      ? "bg-brand-violet text-white border-brand-violet dark:border-brand-violet-on-dark"
                      : "border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:border-brand-violet dark:hover:border-brand-violet-on-dark hover:text-brand-violet dark:hover:text-brand-violet-on-dark"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {loadingTimeline ? (
              <div className="flex flex-col gap-3 pl-16">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="bg-white dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10 p-5 animate-pulse"
                  >
                    <div className="h-4 bg-gray-100 dark:bg-white/5 rounded w-1/2 mb-2" />
                    <div className="h-3 bg-gray-100 dark:bg-white/5 rounded w-1/3" />
                  </div>
                ))}
              </div>
            ) : sortedTimeline.length === 0 ? (
              <div className="bg-white dark:bg-brand-dark-surface border border-dashed border-gray-200 dark:border-white/10 rounded-2xl p-16 text-center">
                <div className="w-12 h-12 bg-brand-violet-tint dark:bg-brand-violet/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-6 h-6 text-brand-violet dark:text-brand-violet-on-dark"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                    />
                  </svg>
                </div>
                <p className="text-gray-400 text-sm mb-1 font-medium">
                  {filterType === "All"
                    ? "Your timeline is empty"
                    : `No ${filterType.toLowerCase()} entries yet`}
                </p>
                <p className="text-gray-400 text-xs mb-6">
                  Add your work experience, education, certifications and more,
                  manually or by importing from LinkedIn.
                </p>

                {filterType === "All" && (
                  <div className="mb-6 rounded-xl bg-white dark:bg-brand-dark-surface border border-gray-100 dark:border-white/10 px-5 py-4 w-full max-w-sm mx-auto text-left">
                    {" "}
                    <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                      Import from LinkedIn
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 leading-relaxed">
                      Go to your LinkedIn profile, click{" "}
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        More (...)
                      </span>{" "}
                      and select{" "}
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        Save to PDF
                      </span>
                      . Then upload that file using the button above.
                    </p>
                    <p className="text-xs text-gray-400">
                      Or add entries one by one using the button below.
                    </p>
                  </div>
                )}

                <button
                  onClick={() => {
                    setEditingEntry(null);
                    setShowEntryPanel(true);
                  }}
                  className="bg-brand-violet text-white px-6 py-2 rounded-lg text-sm hover:opacity-90 transition"
                >
                  + Add first entry
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="absolute left-4 sm:left-5 top-5 bottom-8 w-0.5 bg-gradient-to-b from-brand-violet via-brand-violet/40 to-transparent" />
                <div className="flex flex-col">
                  {sortedTimeline.map((entry, idx) => {
                    const dateRange = formatDateRange(entry);
                    const isLast = idx === sortedTimeline.length - 1;
                    const dotColor = ENTRY_TYPE_DOT_CLASS;
                    return (
                      <div
                        key={entry.id}
                        className={`flex gap-3 sm:gap-6 ${!isLast ? "mb-4 sm:mb-6" : ""}`}
                      >
                        <div className="flex flex-col items-center flex-shrink-0 w-8 sm:w-10">
                          <div
                            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-base flex-shrink-0 z-10 shadow-sm ${dotColor}`}
                          >
                            {ENTRY_TYPE_ICONS[entry.entry_type] ?? "📋"}
                          </div>
                          {entry.started_at && (
                            <span className="text-xs text-gray-400 mt-1 text-center leading-tight">
                              {new Date(entry.started_at).getFullYear()}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0 pb-1">
                          <div className="bg-white dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10 p-5 hover:shadow-sm transition">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <span className="font-semibold text-sm text-gray-900 dark:text-white">
                                    {entry.title}
                                  </span>
                                  {entry.organisation && (
                                    <>
                                      <span className="text-gray-300 dark:text-gray-600 text-xs">
                                        ·
                                      </span>
                                      <span className="text-gray-500 dark:text-gray-400 text-sm">
                                        {entry.organisation}
                                      </span>
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  {dateRange && (
                                    <span className="text-xs text-gray-400">
                                      {dateRange}
                                    </span>
                                  )}
                                  {entry.location && (
                                    <span className="text-xs text-gray-400">
                                      · {entry.location}
                                    </span>
                                  )}
                                  <ValidationBadge
                                    status={entry.validation_status}
                                  />
                                </div>
                                {entry.description && (
                                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 line-clamp-2 leading-relaxed">
                                    {entry.description}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                  onClick={() => {
                                    setEditingEntry(entry);
                                    setShowEntryPanel(true);
                                  }}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                    />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => setDeleteEntryTarget(entry)}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 transition"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-6 mt-2">
                  <div className="w-10 flex justify-center">
                    <div className="w-3 h-3 rounded-full bg-gray-200 dark:bg-white/10 border-2 border-gray-300 dark:border-white/20" />
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5 italic">
                    Beginning of timeline
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Resumes tab ── */}
        {activeTab === "resumes" && (
          <div>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  My Resumes
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  Each resume is a tailored view of your timeline
                </p>
              </div>
              <button
                onClick={() => {
                  setEditingResume(null);
                  setShowResumePanel(true);
                }}
                className="bg-brand-violet text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition flex items-center gap-2"
              >
                <span className="text-lg leading-none">+</span> New resume
              </button>
            </div>
            <div className="mb-6 bg-brand-violet-tint dark:bg-brand-violet/10 border border-brand-violet/20 dark:border-brand-violet-on-dark/20 rounded-xl px-4 py-3 flex items-center justify-between gap-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                Paste your resume link in every job application — portfolio
                field, cover letter, email signature.
              </p>
              <CopyResumeLink className="flex-shrink-0" />
            </div>

            {loadingResumes ? (
              <div className="flex flex-col gap-3">
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    className="bg-white dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10 p-5 animate-pulse"
                  >
                    <div className="h-4 bg-gray-100 dark:bg-white/5 rounded w-1/3 mb-2" />
                    <div className="h-3 bg-gray-100 dark:bg-white/5 rounded w-1/4" />
                  </div>
                ))}
              </div>
            ) : resumes.length === 0 ? (
              <div className="bg-white dark:bg-brand-dark-surface border border-dashed border-gray-200 dark:border-white/10 rounded-2xl p-16 text-center">
                <div className="w-12 h-12 bg-brand-violet-tint dark:bg-brand-violet/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-6 h-6 text-brand-violet dark:text-brand-violet-on-dark"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <p className="text-gray-400 text-sm mb-1 font-medium">
                  No resumes yet
                </p>
                <p className="text-gray-400 text-xs mb-4">
                  Create your first resume from your timeline entries
                </p>
                <button
                  onClick={() => {
                    setEditingResume(null);
                    setShowResumePanel(true);
                  }}
                  className="bg-brand-violet text-white px-6 py-2 rounded-lg text-sm hover:opacity-90 transition"
                >
                  + Create first resume
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {resumes.map((resume) => (
                  <div
                    key={resume.id}
                    className="bg-white dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10 p-5 flex items-start justify-between gap-4 hover:shadow-sm transition"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-sm text-gray-900 dark:text-white">
                          {resume.name}
                        </span>
                        {resume.is_primary && (
                          <span className="text-xs bg-brand-violet/10 text-brand-violet dark:text-brand-violet-on-dark px-2 py-0.5 rounded-full font-medium">
                            Primary
                          </span>
                        )}
                        {resume.is_public ? (
                          <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400 px-2 py-0.5 rounded-full">
                            Public
                          </span>
                        ) : (
                          <span className="text-xs bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 px-2 py-0.5 rounded-full">
                            Private
                          </span>
                        )}
                      </div>
                      {resume.summary && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                          {resume.summary}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        Created{" "}
                        {new Date(resume.created_at).toLocaleDateString(
                          "en-SE",
                        )}
                        {resume.is_public && (
                          <span>
                            {" "}
                            ·{" "}
                            <a
                                href={`/r/${resume.slug}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-brand-violet dark:text-brand-violet-on-dark hover:underline"
                              >
                                joborigo.com/r/{resume.slug}
                              </a>
                              {/* Per-resume link. Points at this exact document,
                                  so an employer sees what was actually sent
                                  rather than whatever is current. */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigator.clipboard?.writeText(
                                    `https://joborigo.com/r/${resume.slug}`,
                                  );
                                  setCopiedSlug(resume.slug);
                                  setTimeout(() => setCopiedSlug(null), 2000);
                                }}
                                className="ml-2 text-xs text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark"
                              >
                                {copiedSlug === resume.slug ? "Copied" : "Copy"}
                              </button>
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {resume.is_public && (
                        <button
                          onClick={() => setQrResume(resume)}
                          title="QR code & share link"
                          className="p-1.5 rounded-lg text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                            />
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setEditingResume(resume);
                          setShowResumePanel(true);
                        }}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => setDeleteResumeTarget(resume)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 transition"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Arena tab ── */}
        {activeTab === "arena" && (
          <div>
            <div className="mb-6 bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-6">
              <h2 className="font-semibold text-gray-900 dark:text-white mb-1">
                Candidate Arena
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
                Opt in and let employers find you — without revealing who you
                are on Joborigo. Your arena profile shows only your skills,
                availability, and preferences. No name, no gender, no photo.
                Note: if an employer integrates via API using your email
                address, your email may be visible to them.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage your arena profile and opt-in settings in{" "}
                <a
                  href="/app/settings"
                  className="text-brand-violet dark:text-brand-violet-on-dark hover:underline font-medium"
                >
                  Account settings →
                </a>
              </p>
            </div>
            <div className="bg-brand-violet-tint dark:bg-brand-violet/10 rounded-2xl border border-brand-violet/20 dark:border-brand-violet-on-dark/20 p-5">
              <h3 className="text-sm font-semibold text-brand-violet dark:text-brand-violet-on-dark mb-3">
                What's never shown to employers
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  "Your name",
                  "Your gender",
                  "Your photo",
                  "Your age",
                  "Your nationality",
                  "Your exact location",
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
                  >
                    <span className="text-brand-violet dark:text-brand-violet-on-dark font-bold">✓</span>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />

      {showEntryPanel && (
        <EntryPanel
          entry={editingEntry}
          onClose={() => {
            setShowEntryPanel(false);
            setEditingEntry(null);
          }}
          onSave={fetchTimeline}
        />
      )}

      {showResumePanel && (
        <ResumePanel
          resume={editingResume}
          timeline={timeline}
          onClose={() => {
            setShowResumePanel(false);
            setEditingResume(null);
          }}
          onSave={fetchResumes}
        />
      )}

      {qrResume && (
        <QRModal resume={qrResume} onClose={() => setQrResume(null)} />
      )}

      {deleteEntryTarget && (
        <ConfirmModal
          title="Delete this entry?"
          message={`"${deleteEntryTarget.title}" will be removed from all resumes. This cannot be undone.`}
          confirmLabel="Delete entry"
          onConfirm={handleDeleteEntryConfirmed}
          onCancel={() => setDeleteEntryTarget(null)}
        />
      )}

      {deleteResumeTarget && (
        <ConfirmModal
          title="Delete this resume?"
          message={`"${deleteResumeTarget.name}" will be permanently deleted. This cannot be undone.`}
          confirmLabel="Delete resume"
          onConfirm={handleDeleteResumeConfirmed}
          onCancel={() => setDeleteResumeTarget(null)}
        />
      )}
    </div>
  );
}
